import { cn } from "@/libs/utils";
import SectionHeader from "@/components/tactsuit/common/SectionHeader";
import { useTrans } from "src/utils/useTrans";
import BleIcon from "src/assets/icons/BleIcon";
import BleAudioIcon from "src/assets/icons/BleAudioIcon";
import FitAllIcon from "src/assets/icons/FitAllIcon";
import VrReadyIcon from "src/assets/icons/VrReadyIcon";
import WirelessIcon from "src/assets/icons/WirelessIcon";
import CrossPlatformIcon from "src/assets/icons/CrossPlatformIcon";
import LongBatteryIcon from "src/assets/icons/LongBatteryIcon";
import MeshIcon from "src/assets/icons/MeshIcon";
import SuitUpIcon from "src/assets/icons/SuitUpIcon";
import WaterRepellantIcon from "src/assets/icons/WaterRepellantIcon";
import VRIcon from "src/assets/icons/VRIcon";
import SizeIcon from "src/assets/icons/SizeIcon";
import { Text } from "src/common/Text";
import Link from "next/link";
import { Icon } from "src/common/Icon";

const Label = ({ text, className = "" }) =>
  Text({
    className: cn(
      "whitespace-pre-wrap text-center text-sm md:text-base",
      className,
    ),
    text,
  });

const summary = {
  "tactglove-dk2": [
    {
      icon: <Icon name={"haptic"} />,
      label: "12 Haptic Feedback Points",
      details: ["12 HD LRAs (High definition LRA)"],
    },
    {
      icon: <Icon name="camerat-based-tracking" />,
      label: "Compatible w/Camera Based Hand Tracking",
    },
    {
      icon: <Icon name="delicate-control" />,
      label: "DELICATE CONTROL OF HAPTIC MOTORS",
    },
    {
      icon: <Icon name="ble-connectivity" />,
      label: "Bluetooth Connectivity",
    },
    { icon: <Icon name="4-sizes" />, label: "4 SIZES" },
    {
      icon: <Icon name={"inner-gloves"} />,
      label: "INNER GLOVES FOR HYGIENE",
    },
    {
      icon: <Icon name="anti-slip" />,
      label: "ANTI-SLIP AND TOUCHSCREEN COMPATIBLE FINGERTIPS",
    },
    { icon: <LongBatteryIcon />, label: "3.5 Hours Playtime" },
  ],
  "tactosy-for-arms": [
    { icon: <Icon name={"haptic"} />, label: "12 Haptic Feedback Points" },
    { icon: <BleIcon />, label: "Bluetooth Connectivity" },
    { icon: <LongBatteryIcon />, label: "Long Battery Life" },
    { icon: <MeshIcon />, label: "Detachable & Washable Mesh lining" },
    { icon: <SuitUpIcon />, label: "Quick Suit Up" },
    { icon: <WaterRepellantIcon />, label: "Water-resistant fabric" },
  ],
  "tactosy-for-feet": [
    { icon: <Icon name={"haptic"} />, label: "6 Haptic Points" },
    { icon: <BleIcon />, label: "Bluetooth Connectivity" },
    { icon: <LongBatteryIcon />, label: "Long Battery Life" },
    { icon: <MeshIcon />, label: "Detachable & Washable Mesh lining" },
    { icon: <SuitUpIcon />, label: "Quick Suit Up" },
    { icon: <WaterRepellantIcon />, label: "Water-resistant fabric" },
  ],
  tactvisor: [
    {
      icon: <Icon name={"haptic"} />,
      label: "4 HAPTIC FEEDBACK points",
      details: ["4 ERM motors"],
    },
    {
      icon: <BleIcon />,
      label: "LAG-FREE",
      details: ["Bluetooth 4.0 (BLE)"],
    },
    {
      icon: <VRIcon />,
      label: "COMPATIBLE WITH",
      details: ["• Meta Quest 3*, Meta Quest 2*, Valve Index, Rift S*"],
      comments: ["* Facial Interface from a 3rd party is required"],
    },
    {
      icon: <WaterRepellantIcon />,
      label: "WATER-RESISTANT PU LEATHER FOAM",
    },
    { icon: <VrReadyIcon />, label: "VR READY w/ EMBEDDED HAPTICS" },
    { icon: <Icon name={"play-time"} />, label: "8 HOURS OF PLAY TIME" },
    {
      icon: <LongBatteryIcon />,
      label: "RECHARGEABLE BATTERY",
      details: ["Li-ion rechargeable Battery: 3.8 V, 630mAh, 2.394Wh"],
    },
    {
      icon: <SizeIcon />,
      label: "SIZE & WEIGHT",
      details: [
        "Inner : 19.4cm x 5.8cm / 7.6” x 2.3” Outer : 23.4cm x 10.9cm / 9.2” x 4.3” Weight : 63g / 0.14lbs",
      ],
    },
  ],
  "tactsuit-x40": [
    {
      icon: <Icon name={"haptic"} />,
      label: "40 HAPTIC FEEDBACK points",
      details: ["40 ERM motors"],
    },
    {
      icon: <BleAudioIcon />,
      label: "Dual Connectivity",
      details: ["Bluetooth 4.0 (BLE)", "3.5mm audio jack"],
    },
    {
      icon: <WirelessIcon />,
      label: "LAG-FREE WIRELESS CONNECTIVITY",
      details: ["Wireless Connection"],
    },
    {
      icon: <LongBatteryIcon />,
      label: "RECHARGEABLE BATTERY",
      details: [
        "Li-ion Battery : 3.63V, 9800mAh, 35.574Wh",
        "Charging : 5hrs w/ 5V 2A (Max) USB Type C",
      ],
    },
    {
      icon: <FitAllIcon />,
      label: "One-size-fits-all",
      details: [
        "Size free (25” ~ 50”): Adjustable with shoulder snap buttons Closed Length : 57cm / 22.5” Opened Length : 61cm / 24” Weight : 1.7kg / 3.7lbs",
      ],
    },
    {
      icon: <CrossPlatformIcon />,
      label: "Cross-Platform Support",
      details: [],
    },
    {
      icon: <MeshIcon />,
      label: "Detachable & Washable Mesh lining",
      details: [],
    },
    { icon: <SuitUpIcon />, label: "12 HOURS OF PLAY TIME", details: [] },
  ],
  "tactsuit-x16": [
    {
      icon: <Icon name={"haptic"} />,
      label: "16 HAPTIC FEEDBACK points",
      details: ["16 ERM motors"],
    },
    {
      icon: <BleAudioIcon />,
      label: "Dual Connectivity",
      details: ["Bluetooth 4.0 (BLE)", "3.5mm audio jack"],
    },
    {
      icon: <WirelessIcon />,
      label: "LAG-FREE",
      details: ["Wireless Connection"],
    },
    {
      icon: <LongBatteryIcon />,
      label: "RECHARGEABLE BATTERY",
      details: [
        "Li-ion Battery : 3.63V, 4900mAh, 17.787Wh",
        "Charging : 2.5hrs w/ 5V 2A (Max) USB Type C",
      ],
    },
    {
      icon: <FitAllIcon />,
      label: "ONE-SIZE-FITS-ALL",
      details: [
        `Size free (25” ~ 50”): Adjustable with shoulder snap buttons Closed Length : 50cm / 19.7” Opened Length : 53cm / 20.8” Weight : 0.95kg / 2.1lbs`,
      ],
    },
    {
      icon: <CrossPlatformIcon />,
      label: "Cross-Platform Support",
      details: [],
    },
    {
      icon: <MeshIcon />,
      label: "Detachable & Washable Mesh lining",
      details: [],
    },
    { icon: <SuitUpIcon />, label: "12 HOURS OF PLAY TIME", details: [] },
  ],
};

export default function Summary({
  className = "",
  id,
}: {
  className?: string;
  id: string;
}) {
  const { t } = useTrans(["tactsuit"]);

  if (!summary[id]) {
    return null;
  }

  const cols = summary[id].length / 2;
  const translatedData = summary[id].map((item) => ({
    ...item,
    label: t(item.label),
    details: item.details?.map((d) => t(d)),
    comments: item.comments?.map((d) => t(d)),
  }));

  return (
    <>
      {/* 시안의 **"Tech Specs" 섹션 헤더**(Feet `28716:155752` / Visor 동일 위치).
          그리드만 제목 없이 떠 있었다(#5266).

          ⚠️ 헤더를 호출처가 아니라 **여기** 두는 이유: 위 `if (!summary[id]) return null`
          에 자동으로 묶인다. 호출처에 두면 데이터 없는 제품이 이 분기에 추가될 때
          **헤더만 덩그러니** 남는다(현재 Visor·Feet 은 둘 다 데이터 있음).

          ⚠️ 아래 `TactsuitTechSpecs` 안의 `TechSpecs` 가 그리는 `{제품명} Specs` 와는
          **다른 섹션**이다(Feet y=7769 "Tactosy for feet Specs"). 이름이 비슷하다고
          중복으로 보고 지우지 말 것. */}
      {/* 모바일 제목↔그리드 간격 20 (`28718:173289`: 제목 h32 → Body y52).
          `SectionHeader` 의 세로 여백은 `laptop:py-6` 뿐이라 모바일에서 0 이었다.
          데스크톱은 그 `py-6`(24)이 시안 헤더 88 = 24 + 텍스트 40 + 24 와 맞으므로
          `laptop:mb-0` 으로 되돌린다 — 같은 파일군의 "Additional Accessories" 헤더
          (`TactsuitTechSpecs`)가 쓰는 관례와 동일하다. */}
      <SectionHeader className="mb-5 laptop:mb-0 laptop:text-headline-lg">
        {t("Tech Specs")}
      </SectionHeader>
      <div
        className={cn(
          cols !== 3
            ? `max-w-screen-lg grid-cols-2 sm:grid-cols-3 md:grid-cols-4`
            : "max-w-screen-md grid-cols-2 sm:grid-cols-3",
          "mx-auto grid w-full gap-x-3 laptop:gap-x-6",
          className,
        )}
      >
        {translatedData.map((value, index) => {
          const { label, icon, details, comments } = value;
          return (
            <div
              className={"flex flex-col items-center pt-6 laptop:min-h-[240px]"}
              key={index}
            >
              {!!icon && (
                <div
                  className={
                    "flex h-20 w-20 transform items-center justify-center"
                  }
                >
                  {icon}
                </div>
              )}
              {!!label &&
                (label === "COMPATIBLE WITH" ? (
                  <Link href="/support/faqs?categories=compatibility&ID=what-vr-headsets-are-bhaptics'-device-compatible-with">
                    <div className="group mb-3 mt-6 flex cursor-pointer items-center gap-x-1">
                      <Label
                        className="group-hover:underline"
                        text={t(label)}
                      />
                      <Icon name="question" />
                    </div>
                  </Link>
                ) : (
                  <Label text={t(label)} className={"mb-3 mt-6"} />
                ))}
              {!!details && (
                <span
                  className={
                    "flex h-fit flex-col whitespace-pre-wrap text-center text-xs"
                  }
                >
                  {details.map((d, index) => (
                    <div key={index}>{t(d)}</div>
                  ))}
                </span>
              )}
              {!!comments && (
                <span
                  className={
                    "text-222222/50 mt-1 flex h-fit flex-col whitespace-pre-wrap text-center text-xs font-400"
                  }
                >
                  {comments.map((d, index) => (
                    <div key={index}>{t(d)}</div>
                  ))}
                </span>
              )}
            </div>
          );
        })}
      </div>
    </>
  );
}
